import {
	DeleteFilled,
	EditFilled,
	LeftOutlined,
	RightOutlined,
} from "@ant-design/icons"
import { BackButton } from "components/BackButton/BackButton"
import { Button } from "components/Button/Button"
import { DatePicker } from "components/DatePicker/DatePicker"
import { Divider } from "components/Divider/Divider"
import { Drawer, type DrawerProps } from "components/Drawer/Drawer"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Form, type FormRule } from "components/Form/Form"
import { InputNumber } from "components/InputNumber/InputNumber"
import { useNotification } from "components/Notification/Notification"
import { Spinner } from "components/Spinner/Spinner"
import { TimePicker } from "components/TimePicker/TimePicker"
import { useDisclosure } from "hooks/useDisclosure"
import { NurseTime } from "interface/patientType"
import { Organization } from "interface/user"
import { sumBy } from "lodash-es"
import { memo, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router"
import {
	useCreateNurseTime,
	useDeleteNurseTime,
	useNurseTimes,
	usePatientDetails,
	useUpdateNurseTime,
} from "services/patients"
import { useOrganization, useUserRole } from "services/users"
import { cn } from "styles/cn"
import { asDate, Dayjs, today } from "utils/date"

export function TimeManagement({ patientId }: { patientId: string }) {
	const manage = useDisclosure()

	return (
		<>
			<Button onClick={manage.toggle}>Time management</Button>

			{manage.isOpen && (
				<Manage patientId={patientId} onClose={manage.toggle} />
			)}
		</>
	)
}

function Manage({
	patientId,
	onClose,
}: {
	patientId: string
	onClose: DrawerProps["onClose"]
}) {
	const { data: role } = useUserRole()
	const organization = useOrganization()
	const [searchParams] = useSearchParams()
	const [month, setMonth] = useState<Dayjs>(
		!!searchParams.get("month") ? asDate(searchParams.get("month")) : today(),
	)
	const startDate = useMemo(() => month.startOf("month").toISOString(), [month])
	const endDate = useMemo(() => month.endOf("month").toISOString(), [month])
	const { data: patient } = usePatientDetails(patientId)
	const { data: nurseTimes = [], isFetching } = useNurseTimes({
		patientId,
		startDate,
		endDate,
	})
	const totalMinutes = useMemo(
		() => sumBy(nurseTimes, "durationInMinutes"),
		[nurseTimes],
	)
	const headerId = `timemanagement-${patient?.id}`
	const listAccessibleName = useMemo(
		() => `${month.format("MMMM")} has ${nurseTimes.length} time logs`,
		[month, nurseTimes],
	)

	if (!role || !organization || !patient) {
		return null
	}

	return (
		<Drawer
			open
			onClose={onClose}
			title={<BackButton onClick={onClose}>{patient.fullName}</BackButton>}
			aria-labelledby={headerId}
		>
			<h2 id={headerId} className="font-headings text-3xl font-semibold">
				Time management
			</h2>

			<div className="flex flex-1 flex-col gap-3 overflow-y-auto">
				<h3 className="font-headings text-xl font-bold">New log</h3>

				<CreateNewLog patientId={patientId} />

				<Divider className="border-grey-20" />

				<div className="flex flex-wrap items-center justify-between gap-2">
					<h3 className="font-headings text-xl font-bold">Time logs</h3>
					<div className="flex flex-row gap-1">
						<Button
							type="text"
							icon={<LeftOutlined aria-hidden />}
							aria-label="Go to previous month"
							onClick={() => setMonth(month.subtract(1, "month"))}
						/>
						<DatePicker.Month
							value={month}
							maxDate={today()}
							className="w-40"
							aria-label="Select month"
							onChange={(date) => date && setMonth(date)}
						/>
						<Button
							type="text"
							icon={<RightOutlined aria-hidden />}
							aria-label="Go to next month"
							disabled={month.isSame(today(), "month")}
							onClick={() => setMonth(month.add(1, "month"))}
						/>
					</div>
				</div>

				<p className="flex gap-2">
					<span className="text-navy-60">Total this month:</span>
					<span className="font-semibold">{totalMinutes} minutes</span>
				</p>

				<Spinner spinning={isFetching} rootClassName="min-h-auto">
					<ul className="flex flex-col gap-3" aria-label={listAccessibleName}>
						{nurseTimes.map((nurseTime) => (
							<ListItem
								key={nurseTime.id}
								nurseTime={nurseTime}
								org={organization}
								currentUserId={role.id}
							/>
						))}
					</ul>
				</Spinner>
			</div>
		</Drawer>
	)
}

const ListItem = memo(function ListItem({
	org,
	currentUserId,
	nurseTime,
}: {
	org: Organization
	currentUserId: string
	nurseTime: NurseTime
}) {
	const day = useMemo(() => asDate(nurseTime.timestamp), [nurseTime.timestamp])
	const dayLabel = useMemo(() => day.format("MMM D, YYYY"), [day])
	const interval = useMemo(() => prettyInterval(nurseTime), [nurseTime])
	const sharedAccessibleName = `${interval.replace(" – ", " to ")} on ${dayLabel}`
	const itemAccessibleName = `The time log covers the period from ${sharedAccessibleName} by ${nurseTime.providerName}`
	const edit = useDisclosure()
	type FormValues = {
		timestamp: Dayjs
		durationInMinutes: NurseTime["durationInMinutes"]
	}
	const [form] = Form.useForm<FormValues>()
	const { notify } = useNotification()
	const updateItem = useUpdateNurseTime()
	const deleteItem = useDeleteNurseTime()
	const canModify =
		isStartDateWithinWindow(day) &&
		(nurseTime.providerId === currentUserId || org.canManageNurseTimes)
	const { current: formName } = useRef(`edit-time-log-${nurseTime.id}`)
	const editInitialValues = useMemo(
		() => ({
			timestamp: day,
			durationInMinutes: nurseTime.durationInMinutes,
		}),
		[day, nurseTime.durationInMinutes],
	)
	const handleEditFinish = async (values: FormValues) => {
		updateItem.mutate(
			{
				id: nurseTime.id,
				timestamp: values.timestamp.toISOString(),
				durationInMinutes: +values.durationInMinutes,
			},
			{
				onSuccess() {
					notify.success({
						message: `Update time log on ${dayLabel}`,
						description: "Time log updated successfully",
					})
					edit.close()
				},
			},
		)
	}
	const handleDelete = async () => {
		const message = `Delete time log on ${dayLabel}`
		deleteItem.mutate(nurseTime.id, {
			onSuccess() {
				notify.success({
					message,
					description: "Time log deleted successfully",
				})
			},
			onError() {
				notify.error({
					message,
					description: `An unexpected error occurred while deleting the time log. Please, try again.`,
				})
			},
		})
	}

	return (
		<li
			className={cn(
				"flex gap-1 rounded-lg border border-mint-20 bg-mint-0 p-4",
				edit.isOpen && "flex-col gap-3",
			)}
			aria-label={itemAccessibleName}
		>
			{!edit.isOpen && (
				<>
					<div className="flex min-w-0 flex-2 flex-col">
						<p>{dayLabel}</p>
						<p className="truncate text-navy-60">{nurseTime.providerName}</p>
					</div>
					<div className="flex flex-1 flex-col whitespace-nowrap">
						<p>{nurseTime.durationInMinutes} mins</p>
						<p className="text-navy-60">{interval}</p>
					</div>
					<div
						className={cn(
							"invisible flex flex-1 items-center justify-end",
							canModify && "visible",
						)}
					>
						<Button
							type="text"
							className="text-navy-40"
							disabled={!canModify}
							icon={<EditFilled aria-hidden />}
							aria-label={`Edit time log from ${sharedAccessibleName}`}
							onClick={edit.open}
						/>
						<Button
							type="text"
							className="text-navy-40"
							disabled={!canModify}
							icon={<DeleteFilled aria-hidden />}
							loading={deleteItem.isPending}
							aria-label={`Delete time log from ${sharedAccessibleName}`}
							onClick={handleDelete}
						/>
					</div>
				</>
			)}

			{edit.isOpen && (
				<>
					<div className="flex items-center justify-between gap-3">
						<p className="font-semibold">{dayLabel}</p>

						<Button
							type="text"
							className="text-navy-40"
							disabled={!canModify}
							icon={<DeleteFilled aria-hidden />}
							loading={deleteItem.isPending}
							onClick={handleDelete}
						/>
					</div>

					<Form<FormValues>
						form={form}
						layout="vertical"
						autoComplete="off"
						name={formName}
						className="flex flex-col gap-3"
						aria-label={`Edit time log from ${sharedAccessibleName}`}
						disabled={updateItem.isPending}
						initialValues={editInitialValues}
						onFinish={handleEditFinish}
					>
						<div className="flex gap-3">
							<Form.Item
								name="timestamp"
								label="Start time"
								className="w-full"
								rules={baseRules}
							>
								<TimePicker
									use12Hours
									aria-label="Start time"
									format="h:mm a"
									allowClear={false}
									needConfirm={false}
									className="w-full"
								/>
							</Form.Item>
							<DurationFormItem />
						</div>
						<Form.Item noStyle>
							<Button
								htmlType="submit"
								type="primary"
								loading={updateItem.isPending}
							>
								Update log
							</Button>
						</Form.Item>
						<ErrorStatus show={updateItem.isError}>
							An unexpected error occurred while updating the time log. Please,
							try again.
						</ErrorStatus>
					</Form>
				</>
			)}
		</li>
	)
})

function CreateNewLog({ patientId }: { patientId: string }) {
	type FormValues = {
		timestamp: Dayjs
		durationInMinutes: NurseTime["durationInMinutes"]
	}
	const now = today()
	const minDate =
		now.date() > LIMIT_DAY_TO_MODIFY
			? now.startOf("month")
			: now.subtract(1, "month").startOf("month")
	const [form] = Form.useForm<FormValues>()
	const { notify } = useNotification()
	const createItem = useCreateNurseTime()
	const { current: formName } = useRef(`create-time-log-${patientId}`)
	const handleFinish = async (values: FormValues) => {
		const { timestamp, durationInMinutes } = values
		createItem.mutate(
			{
				patientId,
				timestamp: timestamp.toISOString(),
				durationInMinutes: +durationInMinutes,
			},
			{
				onSuccess() {
					form.resetFields()
					notify.success({
						message: "Create time log",
						description: `Time log created successfully on ${timestamp.format("MMM D, YYYY h:mm a")}`,
					})
				},
			},
		)
	}

	return (
		<Form<FormValues>
			form={form}
			layout="vertical"
			autoComplete="off"
			name={formName}
			className="flex flex-col gap-3 rounded-lg border border-mint-20 bg-mint-0 p-4"
			aria-label="Create new time log"
			disabled={createItem.isPending}
			initialValues={{
				date: now,
				timestamp: now,
				durationInMinutes: 1,
			}}
			onFinish={handleFinish}
		>
			<div className="flex gap-3">
				<Form.Item
					name="timestamp"
					label="Date and start time"
					className="w-full"
					rules={baseRules}
				>
					<DatePicker
						use12Hours
						aria-label="Date and start time"
						format="MMM D, YYYY h:mm a"
						needConfirm={false}
						className="w-full"
						maxDate={now.endOf("day")}
						minDate={minDate}
						showTime
					/>
				</Form.Item>
				<DurationFormItem />
			</div>
			<Form.Item noStyle>
				<Button
					htmlType="button"
					type="primary"
					loading={createItem.isPending}
					onClick={form.submit}
				>
					Log time
				</Button>
			</Form.Item>
			<ErrorStatus show={createItem.isError}>
				An unexpected error occurred while creating the time log. Please, try
				again.
			</ErrorStatus>
		</Form>
	)
}

function DurationFormItem() {
	return (
		<Form.Item
			name="durationInMinutes"
			label="Duration"
			className="w-full"
			rules={[
				...baseRules,
				{
					type: "number",
					min: 1,
					max: 480,
					transform(value) {
						return value ? Number(value) : undefined
					},
				},
			]}
		>
			<InputNumber suffix="mins" min={1} max={480} className="w-full" />
		</Form.Item>
	)
}

function prettyInterval({ timestamp, durationInMinutes }: NurseTime) {
	const start = asDate(timestamp)

	return `${start.format("hh:mm A")} – ${start.add(durationInMinutes, "minute").format("hh:mm A")}`
}

function isStartDateWithinWindow(timestamp: Dayjs) {
	const now = today()

	return (
		timestamp.isSame(now, "month") ||
		(timestamp.isSame(now.subtract(1, "month"), "month") &&
			now.date() <= LIMIT_DAY_TO_MODIFY)
	)
}

const baseRules: FormRule[] = [{ required: true }]

const LIMIT_DAY_TO_MODIFY = 3
